/**
 * bankDataLocalizedSchema - single source of truth (frontend mirror) for the
 * country-schema-based "localized bank data" field
 * (alv_interested_parties.inv_bank_data_localized, A-Z > Finance).
 *
 * The field holds ONE pipe-delimited, atomic string per licensor carrying BOTH
 * the bank-account identification AND the bank address, in a fixed,
 * country-specific token order. A single record must serve BOTH domestic and
 * international wire transfers for every supported country, so token requirement
 * is context-based (see REQ_* / requiredInContext / paymentScope). Format-
 * agnostic: feeds SDAUSUEB (international) today, can feed XAZV / US-ACH /
 * JP-Zengin / KR domestic files later.
 *
 * Format:   <ISO2>|<token1>|<token2>|...|<tokenN>
 *   - delimiter "|" (pipe), empty optional position = "||"
 *   - tokens are stored in DB column inv_bank_data_localized
 *     (varchar(2048), utf8mb3_unicode_ci): the Unicode BMP only - Kanji, Hangul,
 *     umlauts etc. are fine, but 4-byte characters (rare CJK Ext-B+, emoji) are
 *     NOT supported. Irrelevant for DE/US/GB/JP/KR bank/address data.
 *   - token order fixed per country, prefix is ISO 3166-1 alpha-2
 *
 * NO charset restriction in this validator - per-OUTPUT sanitization happens in
 * the backend generators (SDAUSUEB -> ASCII, etc.).
 *
 * IMPORTANT: structurally duplicated in the backend
 * (alv-backend/.../_service/BankDataLocalizedSchema.php). Any schema change MUST
 * be committed to BOTH repos together.
 *
 * Part of ALVCODE-1259 (international bank file / SDAUSUEB), Stufe 2.
 */

export const DELIMITER = '|'

export const ERR_INVALID_COUNTRY = 'INVALID_COUNTRY'
export const ERR_TOKEN_COUNT = 'TOKEN_COUNT'
export const ERR_TOKEN_FORMAT = 'TOKEN_FORMAT'

const BIC_REGEX = /^[A-Za-z]{6}[A-Za-z0-9]{2}([A-Za-z0-9]{3})?$/

/**
 * Requirement context of a token. A single stored record must serve BOTH a
 * domestic and an international wire transfer, so "required" is not absolute:
 *   - REQ_BOTH      always required (core account identity)
 *   - REQ_INTL      required only for international transfers (BIC, bank address)
 *   - REQ_DOMESTIC  required only for domestic transfers (local routing)
 *   - REQ_OPTIONAL  never enforced
 */
export const REQ_BOTH = 'both'
export const REQ_INTL = 'intl'
export const REQ_DOMESTIC = 'domestic'
export const REQ_OPTIONAL = 'optional'

/**
 * Ordered token definition per supported country.
 * Each token: { key, req(REQ_*), regex(RegExp|null) }.
 * regex is applied ONLY to non-empty tokens.
 */
export const SCHEMAS = {
    DE: [
        { key: 'iban', req: REQ_BOTH, regex: null },
        { key: 'bic', req: REQ_INTL, regex: BIC_REGEX },
        { key: 'street', req: REQ_INTL, regex: null },
        { key: 'zip', req: REQ_INTL, regex: /^\d{5}$/ },
        { key: 'city', req: REQ_INTL, regex: null },
    ],
    // US has NO IBAN: domestic (ACH) uses the 9-digit ABA routing number,
    // international (SWIFT) uses the BIC. SEPARATE slots so one record carries
    // BOTH at once (mirrors JP/KR). Generator prefers BIC, falls back to ABA
    // via "//FW". Keep in sync with BankDataLocalizedSchema.php.
    US: [
        { key: 'routing', req: REQ_DOMESTIC, regex: /^\d{9}$/ },
        { key: 'account', req: REQ_BOTH, regex: null },
        { key: 'accountType', req: REQ_DOMESTIC, regex: null },
        { key: 'street', req: REQ_INTL, regex: null },
        { key: 'suite', req: REQ_OPTIONAL, regex: null },
        { key: 'city', req: REQ_INTL, regex: null },
        { key: 'state', req: REQ_INTL, regex: /^[A-Za-z]{2}$/ },
        { key: 'zip', req: REQ_INTL, regex: /^\d{5}(-\d{4})?$/ },
        { key: 'bic', req: REQ_INTL, regex: BIC_REGEX },
    ],
    GB: [
        { key: 'iban', req: REQ_BOTH, regex: null },
        { key: 'bic', req: REQ_INTL, regex: BIC_REGEX },
        { key: 'street', req: REQ_INTL, regex: null },
        { key: 'locality', req: REQ_OPTIONAL, regex: null },
        { key: 'city', req: REQ_INTL, regex: null },
        { key: 'postcode', req: REQ_INTL, regex: /^[A-Za-z0-9 ]{5,8}$/ },
    ],
    JP: [
        { key: 'bic', req: REQ_INTL, regex: BIC_REGEX },
        { key: 'bankCode', req: REQ_DOMESTIC, regex: /^\d{4}$/ },
        { key: 'branchCode', req: REQ_DOMESTIC, regex: /^\d{3}$/ },
        { key: 'accountType', req: REQ_DOMESTIC, regex: null },
        { key: 'account', req: REQ_BOTH, regex: null },
        { key: 'postalCode', req: REQ_INTL, regex: /^\d{3}-\d{4}$/ },
        { key: 'prefecture', req: REQ_INTL, regex: null },
        { key: 'cityWard', req: REQ_INTL, regex: null },
        { key: 'district', req: REQ_INTL, regex: null },
        { key: 'block', req: REQ_INTL, regex: null },
        { key: 'building', req: REQ_OPTIONAL, regex: null },
        { key: 'floor', req: REQ_OPTIONAL, regex: null },
    ],
    KR: [
        { key: 'bic', req: REQ_INTL, regex: BIC_REGEX },
        { key: 'bankCode', req: REQ_DOMESTIC, regex: /^\d{3}$/ },
        { key: 'account', req: REQ_BOTH, regex: null },
        { key: 'postalCode', req: REQ_INTL, regex: /^\d{5}$/ },
        { key: 'province', req: REQ_INTL, regex: null },
        { key: 'district', req: REQ_INTL, regex: null },
        { key: 'neighborhood', req: REQ_OPTIONAL, regex: null },
        { key: 'road', req: REQ_INTL, regex: null },
        { key: 'building', req: REQ_OPTIONAL, regex: null },
        { key: 'floor', req: REQ_OPTIONAL, regex: null },
    ],
}

/**
 * Whether a token is required in the given validation context.
 * context: 'storage' (default, structural) | 'domestic' | 'international'
 */
export function requiredInContext(req, context) {
    if (context === 'domestic') return req === REQ_BOTH || req === REQ_DOMESTIC
    if (context === 'international') return req === REQ_BOTH || req === REQ_INTL
    return req === REQ_BOTH
}

/**
 * Determine payment scope by comparing the own publishing/label company country
 * (Account > Customer data) with the country prefix of the bank data.
 * Equal -> 'domestic', different -> 'international', missing -> 'unknown'.
 */
export function paymentScope(ownIso2, bankCountryIso2) {
    const a = String(ownIso2 || '').trim().toUpperCase()
    const b = String(bankCountryIso2 || '').trim().toUpperCase()
    if (!a || !b) return 'unknown'
    return a === b ? 'domestic' : 'international'
}

export function supportedCountries() {
    return Object.keys(SCHEMAS)
}

export function getSchema(iso2) {
    if (!iso2) return null
    return SCHEMAS[String(iso2).trim().toUpperCase()] || null
}

/**
 * Validate a raw localized string.
 * Empty input is valid (the field is optional).
 * @param {string} raw
 * @param {('storage'|'domestic'|'international')} [context='storage']
 *   - 'storage' (default): structural only (token count + regex + REQ_BOTH).
 *     Lets a domestic-only OR international-only record be stored.
 *   - 'domestic' / 'international': additionally enforce the context-specific
 *     required tokens (used by the file generators).
 * @returns {{ valid: boolean, error: (string|null), country: (string|null) }}
 */
export function validate(raw, context = 'storage') {
    if (raw === null || raw === undefined || String(raw).trim() === '') {
        return { valid: true, error: null, country: null }
    }

    const parts = String(raw).split(DELIMITER)
    const iso2 = String(parts.shift() || '').trim().toUpperCase()
    const schema = getSchema(iso2)

    if (schema === null) {
        return { valid: false, error: ERR_INVALID_COUNTRY, country: iso2 }
    }
    if (parts.length !== schema.length) {
        return { valid: false, error: ERR_TOKEN_COUNT, country: iso2 }
    }

    for (let i = 0; i < schema.length; i++) {
        const def = schema[i]
        const val = String(parts[i] || '').trim()
        if (val === '') {
            if (requiredInContext(def.req, context)) {
                return { valid: false, error: ERR_TOKEN_COUNT, country: iso2 }
            }
            continue
        }
        if (def.regex !== null && !def.regex.test(val)) {
            return { valid: false, error: ERR_TOKEN_FORMAT, country: iso2 }
        }
    }

    return { valid: true, error: null, country: iso2 }
}

/** Human-readable example string per country (used in the FE hint tooltip). */
export const EXAMPLES = {
    DE: 'DE|DE89370400440532013000|COBADEFFXXX|Bockenheimer Landstrasse 24|60323|Frankfurt am Main',
    US: 'US|021000021|1234567890|Checking|270 Park Avenue||New York|NY|10017|CHASUS33',
    GB: 'GB|GB29NWBK60161331926819|NWBKGB2L|1 Churchill Place|Canary Wharf|London|E14 5HP',
    JP: 'JP|BOTKJPJT|0005|001|Futsu|1234567|100-0005|Tokyo|Chiyoda-ku|Marunouchi|2-1-1|Marunouchi Bldg|5F',
    KR: 'KR|HVBKKRSE|081|123-4567890-12345|04539|Seoul|Jung-gu|Myeong-dong|Sejong-daero 79|Hana Bank Bldg|7F',
}
